import {
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  computed,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

import { AdminAcademicAppointmentService } from '../../../../core/services/academic-appointments/admin-academic-appointment.service';
import { SessionViewService } from '../../../../core/services/academic-appointments/session-view.service';
import {
  AttendanceVerdict,
  SessionAttendance,
} from '../../../../core/services/academic-appointments/session-join.service';
import {
  AcademicAppointment,
  AcademicSchedulingOptions,
  SchedulingSubject,
  displayTime,
  sessionStart,
} from '../../../../core/interfaces/academic-appointment';
import {
  CalendarRange,
  SessionsCalendarComponent,
} from '../../../ui/sessions-calendar/sessions-calendar.component';
import { SessionAudienceComponent } from '../../../ui/session-audience/session-audience.component';
import {
  SubjectChoiceService,
  SubjectPathGroup,
} from '../../../../core/services/utils/subject-choice.service';

import { LocalizedDatePipe } from '../../../../shared/pipes/localized-date.pipe';
/**
 * Calendrier de l'établissement : les séances de tous les professeurs, ou d'un
 * seul. Lecture et contrôle — l'administration consulte le détail de chaque
 * séance et son bilan de présence, mais ne planifie pas à la place des profs.
 */
@Component({
  selector: 'app-admin-calendar',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, SessionsCalendarComponent, SessionAudienceComponent, LocalizedDatePipe],
  templateUrl: './calendar.component.html',
  styleUrl: './calendar.component.css',
})
export class AdminCalendarComponent implements OnInit {
  private readonly service = inject(AdminAcademicAppointmentService);
  readonly view = inject(SessionViewService);
  private readonly subjectChoice = inject(SubjectChoiceService);

  @ViewChild('detailDialog') private detailDialog?: ElementRef<HTMLDialogElement>;

  readonly loading = signal(true);
  readonly errored = signal(false);
  readonly sessions = signal<AcademicAppointment[]>([]);
  readonly options = signal<AcademicSchedulingOptions>({
    subjects: [], groups: [], students: [], professors: [],
  });

  /** La période que la grille affiche — ce qu'on recharge quand un filtre change. */
  private range: CalendarRange | null = null;

  readonly selected = signal<AcademicAppointment | null>(null);
  readonly attendance = signal<SessionAttendance | null>(null);
  readonly attendanceLoading = signal(false);

  // Filtres — `null` signifie « tous », l'état par défaut.
  filterProfessorId: number | null = null;
  filterSubjectId: number | null = null;
  filterGroupId: number | null = null;
  includeCancelled = false;

  /**
   * Des filtres sont posés. Un accesseur, pas un `computed` : les champs liés
   * par ngModel ne sont pas des signaux, un `computed` n'aurait aucun
   * producteur et resterait figé à sa première valeur.
   */
  get hasFilters(): boolean {
    return this.filterProfessorId !== null
       || this.filterSubjectId !== null
       || this.filterGroupId !== null
       || this.includeCancelled;
  }

  /** Compteurs de la période affichée, calculés sur les séances chargées. */
  readonly stats = computed(() => {
    const list = this.sessions();
    const minutes = list.reduce((sum, s) => sum + (s.durationMinutes ?? 0), 0);
    const professors = new Set(
      list.map((s) => s.consultantProfessorId).filter((id): id is number => !!id),
    );
    const online = list.filter((s) => !!s.meetingUrl).length;
    return {
      count: list.length,
      hours: Math.round((minutes / 60) * 10) / 10,
      professors: professors.size,
      online,
    };
  });

  ngOnInit(): void {
    this.loadOptions();
  }

  // ─── Période et filtres ────────────────────────────────────────────

  /** La grille affiche une autre période : on la charge. */
  onRange(range: CalendarRange): void {
    this.range = range;
    this.load();
  }

  reload(): void {
    this.load();
  }

  applyFilters(): void {
    this.load();
  }

  clearFilters(): void {
    this.filterProfessorId = null;
    this.filterSubjectId = null;
    this.filterGroupId = null;
    this.includeCancelled = false;
    this.applyFilters();
  }

  // ─── Chargement ────────────────────────────────────────────────────

  private loadOptions(): void {
    this.service.options().subscribe({
      next: (res) => {
        if (res?.data) this.options.set(res.data);
      },
      error: () => {
        // Sans référentiel, le calendrier reste consultable — seuls les
        // filtres perdent leurs listes.
        this.options.set({ subjects: [], groups: [], students: [], professors: [] });
      },
    });
  }

  /** Requête de l'agenda en vol — une seule à la fois. */
  private listRequest?: Subscription;

  private load(): void {
    if (!this.range) return;
    // Changer de semaine puis de filtre lançait deux lectures : la plus lente
    // gagnait, et la grille montrait les séances d'une autre période ou d'un
    // autre enseignant que ceux affichés.
    this.listRequest?.unsubscribe();
    this.loading.set(true);
    this.errored.set(false);

    this.listRequest = this.service
      .calendar({
        from: this.range.from,
        to: this.range.to,
        professorId: this.filterProfessorId,
        subjectId: this.filterSubjectId,
        groupId: this.filterGroupId,
        includeCancelled: this.includeCancelled,
      })
      .subscribe({
        next: (res) => {
          this.sessions.set(res?.data ?? []);
          this.loading.set(false);
        },
        error: () => {
          // Aucune séance de substitution : la grille reste vide.
          this.sessions.set([]);
          this.errored.set(true);
          this.loading.set(false);
        },
      });
  }

  // ─── Détail et présence ────────────────────────────────────────────

  /** La grille rend la séance telle qu'elle l'a reçue : on retrouve la fiche complète. */
  onSelect(picked: { id: number }): void {
    const full = this.sessions().find((s) => s.id === picked.id);
    if (full) this.openDetail(full);
  }


  openDetail(s: AcademicAppointment): void {
    this.selected.set(s);
    this.attendance.set(null);
    this.attendanceLoading.set(true);

    // `<dialog>` + showModal() : le panneau est peint dans la couche
    // supérieure, hors de tout contexte d'empilement de la coquille.
    const el = this.detailDialog?.nativeElement;
    if (el && !el.open) el.showModal();

    this.service.attendance(s.id).subscribe({
      next: (res) => {
        // Le tiroir a pu changer de séance pendant la requête.
        if (this.selected()?.id === s.id) this.attendance.set(res?.data ?? null);
        this.attendanceLoading.set(false);
      },
      error: () => {
        this.attendance.set(null);
        this.attendanceLoading.set(false);
      },
    });
  }

  closeDetail(): void {
    this.detailDialog?.nativeElement.close();
  }

  /** Reflète dans l'état la fermeture faite par le navigateur (Échap, close()). */
  onDetailClosed(): void {
    this.selected.set(null);
    this.attendance.set(null);
  }

  /** Cible du `mousedown` en cours — voir `onBackdropClick`. */
  private pressOrigin: EventTarget | null = null;

  onPressStart(event: MouseEvent): void {
    this.pressOrigin = event.target;
  }

  /**
   * Un clic sur le fond d'un `<dialog>` a pour cible le dialogue lui-même :
   * c'est la façon canonique de fermer au clic extérieur, sans voile maison.
   *
   * On exige que le geste ait **commencé** sur le fond, pour qu'une sélection
   * de texte relâchée en dehors du tiroir ne le referme pas.
   */
  onBackdropClick(event: MouseEvent, dialog: HTMLDialogElement): void {
    const startedOnBackdrop = this.pressOrigin === dialog || this.pressOrigin === null;
    this.pressOrigin = null;
    if (event.target === dialog && startedOnBackdrop) dialog.close();
  }

  verdictIcon(verdict: AttendanceVerdict): string {
    switch (verdict) {
      case 'Held':      return 'fa-solid fa-circle-check';
      case 'Disputed':  return 'fa-solid fa-triangle-exclamation';
      case 'NotHeld':   return 'fa-solid fa-circle-xmark';
      case 'Pending':   return 'fa-solid fa-hourglass-half';
      case 'Cancelled': return 'fa-solid fa-ban';
      default:          return 'fa-solid fa-location-dot';
    }
  }

  // ─── Helpers de gabarit ────────────────────────────────────────────

  time(value: string | null | undefined): string {
    return displayTime(value);
  }

  start(s: AcademicAppointment): string {
    return sessionStart(s);
  }

  /**
   * Libellé d'une matière du filtre.
   *
   * <p>Le référentiel de l'administration couvre tout l'établissement : l'anglais
   * y figure trois fois, une par parcours. Le filtre affichait trois entrées
   * identiques et n'en restituait qu'une — celle qu'on n'avait pas visée une
   * fois sur trois. Le parcours n'est ajouté qu'aux noms réellement ambigus.</p>
   */
  subjectLabel(s: SchedulingSubject): string {
    return this.subjectChoice.label(s, this.options().subjects);
  }

  /** Matières du filtre, regroupées par parcours. */
  readonly subjectGroups = computed<SubjectPathGroup[]>(() =>
    this.subjectChoice.groupByPath(this.options().subjects),
  );

  /** Taille lisible d'un enregistrement. */
  fileSize(bytes: number | null | undefined): string {
    if (!bytes) return '';
    const units = ['o', 'Ko', 'Mo', 'Go'];
    let value = bytes;
    let unit = 0;
    while (value >= 1024 && unit < units.length - 1) {
      value /= 1024;
      unit++;
    }
    return `${value.toFixed(value >= 10 || unit === 0 ? 0 : 1)} ${units[unit]}`;
  }
}

